import { useEffect, useMemo, useRef } from 'react';
import type { SimulationActivity } from '../../../../types/simulation';
import {
  inferActivityBoardingAndAlightingCounts,
  inferActivityPassengerDelta,
  inferActivityStopKind,
} from '../../../../utils/simulation/inferActivityStopKind';
import {
  buildSyntheticActivityForLeg,
  extractPassengerLegsFromTrips,
} from '../../../../utils/simulation/expandActivitiesForExport';
import { trackAnalyticsEvent } from '../../../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../../../services/eventTaxonomy';
import { lookupShiftLabel } from '../../../../services/simulation/shiftLabelRegistry';

export type ActivityNodeType = 'PICK' | 'DROP' | 'SHIFT_START' | 'SHIFT_END' | 'OTHER';

export interface ActivityNode {
  type: ActivityNodeType;
  timeMs: number;
  label: string;
  activity: SimulationActivity;
}

export interface VehicleOperation {
  vehicleId: string;
  displayName: string;
  activities: SimulationActivity[];
  nodes: ActivityNode[];
  maxPax: number;
  totalActivities: number;
  shiftStart: number;
  shiftEnd: number;
  activeHours: number;
  totalDistanceKm: number;
  emissionsKg: number;
  missingCoordinateSegments: number;
}

export const DEFAULT_CO2_RATE_KG_PER_KM = 0.2;
export const CO2_RATE_HELP_TEXT =
  'CO2 rate in kg per km. Default 0.2 represents a medium-sized bus.';
export const CO2_RATE_INVALID_HELP_TEXT =
  'Enter a valid non-negative number. Falling back to 0.2 kg/km.';

export interface VehicleOperationsOptions {
  co2RateKgPerKm?: number | null;
}

export interface VehicleOperationsResult {
  vehicles: VehicleOperation[];
  simStartMs: number;
  simEndMs: number;
  totalDurationMs: number;
  fleetTotalDistanceKm: number;
  fleetTotalEmissionsKg: number;
  co2RateKgPerKm: number;
  isCo2RateValid: boolean;
  co2RateError: string | null;
  missingCoordinateSegments: number;
  processingError: string | null;
}

export const getVehicleKey = (a: SimulationActivity): string =>
  (a.vehicle_id ?? a.vehicle_shift_id ?? a.shift_id ?? 'global').toString();

// Reject timestamps outside this window so a single bad activity (e.g. epoch 0,
// a numeric seconds value parsed as ms, or a far-future sentinel) cannot inflate
// totalDurationMs to an astronomical value that freezes the browser.
const MIN_VALID_MS = new Date('2000-01-01T00:00:00Z').getTime();
const MAX_VALID_MS = new Date('2100-01-01T00:00:00Z').getTime();

/**
 * Extracts a millisecond timestamp from a simulation activity by checking
 * several candidate fields. Returns null when no valid timestamp is found or
 * when the parsed value falls outside the reasonable 2000–2100 range.
 */
function extractTimeMs(a: SimulationActivity): number | null {
  const fields = [
    a.activity_start_time,
    a.activity_start,
    a.min_activity_start_time,
    a.start_time,
    a.time,
  ];
  for (const field of fields) {
    if (field) {
      const parsed = Date.parse(field as string);
      if (!Number.isNaN(parsed) && parsed >= MIN_VALID_MS && parsed <= MAX_VALID_MS) {
        return parsed;
      }
    }
  }
  return null;
}

function toNonNegativeCount(value: unknown): number | null {
  const n =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : NaN;
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  return Math.floor(n);
}

function getPassengersArrayCount(a: SimulationActivity): number | null {
  const raw = a as Record<string, unknown>;
  if (!Array.isArray(raw.passengers)) {
    return null;
  }
  if (raw.passengers.length === 0) {
    return 0;
  }
  let total = 0;
  for (const passenger of raw.passengers) {
    if (passenger != null && typeof passenger === 'object') {
      const count = toNonNegativeCount(
        (passenger as Record<string, unknown>).number_of_passengers
      );
      total += count ?? 1;
      continue;
    }
    total += 1;
  }
  return total;
}

function getExplicitPassengerCount(
  a: SimulationActivity,
  kind: 'pick' | 'drop'
): number | null {
  const raw = a as Record<string, unknown>;
  const baseFieldNames =
    kind === 'pick'
      ? [
          'number_of_rides_picked_up',
          'pick_count',
          'pickup_count',
          'pickup_passengers_count',
          'picked_up_passengers_count',
          'passengers_picked_up_count',
          'number_of_passengers_picked_up',
        ]
      : [
          'number_of_rides_dropped_off',
          'drop_count',
          'dropoff_count',
          'drop_off_count',
          'dropoff_passengers_count',
          'passengers_dropped_off_count',
          'number_of_passengers_dropped_off',
        ];
  for (const fieldName of baseFieldNames) {
    const count = toNonNegativeCount(raw[fieldName]);
    if (count !== null) {
      return count;
    }
  }

  const usersField = kind === 'pick' ? raw.users_picked_up : raw.users_dropped_off;
  if (Array.isArray(usersField)) {
    return usersField.length;
  }

  const standardCount = toNonNegativeCount(raw.standard_passengers ?? raw.standard_count);
  const wheelchairCount = toNonNegativeCount(
    raw.wheelchair_passengers ?? raw.wheelchair_count
  );
  if (standardCount !== null || wheelchairCount !== null) {
    return (standardCount ?? 0) + (wheelchairCount ?? 0);
  }

  const totalCount = toNonNegativeCount(raw.passengers_count ?? raw.number_of_passengers);
  if (totalCount !== null) {
    return totalCount;
  }

  return getPassengersArrayCount(a);
}

function getExplicitOccupancy(a: SimulationActivity): number | null {
  const raw = a as Record<string, unknown>;
  const occupancyFields = [
    raw.pax_on_board,
    raw.passengers_on_board,
    raw.current_passengers,
    raw.onboard_passengers,
    raw.occupancy,
    raw.passenger_load,
  ];
  for (const field of occupancyFields) {
    const count = toNonNegativeCount(field);
    if (count !== null) {
      return count;
    }
  }
  return null;
}

function haversineKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function getLat(a: SimulationActivity): number | undefined {
  const n =
    a.activity_latitude ??
    a.latitude ??
    a.lat;
  return typeof n === 'number' ? n : undefined;
}

function getLng(a: SimulationActivity): number | undefined {
  const n =
    a.activity_longitude ??
    a.longitude ??
    a.lng;
  return typeof n === 'number' ? n : undefined;
}

function formatTimeMs(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCHours().toString().padStart(2, '0')}:${d.getUTCMinutes().toString().padStart(2, '0')}`;
}

export function useVehicleOperations(
  activities: SimulationActivity[] | null | undefined,
  options?: VehicleOperationsOptions
): VehicleOperationsResult {
  const result = useMemo(() => {
    const isCo2RateValid =
      typeof options?.co2RateKgPerKm === 'number' &&
      Number.isFinite(options.co2RateKgPerKm) &&
      options.co2RateKgPerKm >= 0;
    const co2RateKgPerKm = isCo2RateValid
      ? options!.co2RateKgPerKm!
      : DEFAULT_CO2_RATE_KG_PER_KM;
    const co2RateError =
      options?.co2RateKgPerKm == null || isCo2RateValid
        ? null
        : CO2_RATE_INVALID_HELP_TEXT;

    try {
    const list = activities ?? [];
    if (list.length === 0) {
      return {
        vehicles: [],
        simStartMs: 0,
        simEndMs: 0,
        totalDurationMs: 0,
        fleetTotalDistanceKm: 0,
        fleetTotalEmissionsKg: 0,
        co2RateKgPerKm,
        isCo2RateValid,
        co2RateError,
        missingCoordinateSegments: 0,
        processingError: null,
      };
    }

    const byVehicle = new Map<string, SimulationActivity[]>();
    // Track a human-readable label per vehicle key. Priority:
    //   1. `shift_name` on the activity (API metadata, if present)
    //   2. `vehicle_name` on the activity (API metadata, if present)
    //   3. label registered client-side in the shift label registry, looked
    //      up by any of the activity's shift/vehicle ids
    // Falls back to "Vehicle N" further down when no label resolves.
    const labelByVehicleKey = new Map<string, string>();
    list.forEach((a) => {
      const key = getVehicleKey(a);
      if (!byVehicle.has(key)) byVehicle.set(key, []);
      byVehicle.get(key)!.push(a);

      if (!labelByVehicleKey.has(key)) {
        const activityShiftName =
          typeof a.shift_name === 'string' && a.shift_name.trim() !== ''
            ? a.shift_name.trim()
            : null;
        const activityVehicleName =
          typeof a.vehicle_name === 'string' && a.vehicle_name.trim() !== ''
            ? a.vehicle_name.trim()
            : null;
        // The simulator may echo our outgoing ids back under any of several
        // field names depending on the endpoint version. Check the full
        // fan-out so a contract drift on one field doesn't silently strand
        // the label and force "Vehicle N" fallback.
        const registeredLabel = lookupShiftLabel([
          a.vehicle_shift_id,
          a.shift_id,
          a.vehicle_id,
          a.driver_id as string | null | undefined,
          a.external_driver_id as string | null | undefined,
          a.vehicle_external_reference_id as string | null | undefined,
          a.external_reference_id as string | null | undefined,
        ]);
        const resolved = activityShiftName ?? activityVehicleName ?? registeredLabel;
        if (resolved) {
          labelByVehicleKey.set(key, resolved);
        }
      }
    });

    const vehicles: VehicleOperation[] = [];
    let simStartMs = Number.MAX_SAFE_INTEGER;
    let simEndMs = Number.MIN_SAFE_INTEGER;
    let fleetTotalDistanceKm = 0;
    let missingCoordinateSegments = 0;

    byVehicle.forEach((actList, vehicleId) => {
      // Attach resolved timestamps once so we don't call extractTimeMs repeatedly.
      const withTimes = actList.map((a) => ({ a, timeMs: extractTimeMs(a) }));
      // Sort: activities with valid timestamps first, invalid ones last.
      const sorted = withTimes
        .sort((x, y) => (x.timeMs ?? Number.MAX_SAFE_INTEGER) - (y.timeMs ?? Number.MAX_SAFE_INTEGER))
        .map(({ a }) => a);

      // Only valid timestamps contribute to the sim time bounds.
      const validTimes = withTimes
        .map(({ timeMs }) => timeMs)
        .filter((t): t is number => t !== null);
      const shiftStart = validTimes.length > 0 ? Math.min(...validTimes) : 0;
      const shiftEnd = validTimes.length > 0 ? Math.max(...validTimes) : 0;

      if (shiftStart > 0) simStartMs = Math.min(simStartMs, shiftStart);
      if (shiftEnd > 0) simEndMs = Math.max(simEndMs, shiftEnd);

      let totalDistanceKm = 0;
      let missingVehicleCoordinateSegments = 0;
      for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1];
        const curr = sorted[i];
        const lat1 = getLat(prev);
        const lng1 = getLng(prev);
        const lat2 = getLat(curr);
        const lng2 = getLng(curr);
        if (
          lat1 != null &&
          lng1 != null &&
          lat2 != null &&
          lng2 != null
        ) {
          totalDistanceKm += haversineKm(lat1, lng1, lat2, lng2);
        } else {
          missingVehicleCoordinateSegments += 1;
        }
      }
      fleetTotalDistanceKm += totalDistanceKm;
      missingCoordinateSegments += missingVehicleCoordinateSegments;

      // Passenger count precedence:
      // 1) explicit occupancy/count fields
      // 2) passengers array-derived counts
      // 3) pickup/dropoff fallback heuristic (+1 / -1)
      let runningPax = 0;
      let maxPax = 0;
      sorted.forEach((a) => {
        const explicitOccupancy = getExplicitOccupancy(a);
        if (explicitOccupancy !== null) {
          runningPax = explicitOccupancy;
          maxPax = Math.max(maxPax, runningPax);
          return;
        }
        const inferredDelta = inferActivityPassengerDelta(a);
        if (inferredDelta !== null) {
          runningPax += inferredDelta;
          runningPax = Math.max(0, runningPax);
          maxPax = Math.max(maxPax, runningPax);
          return;
        }
        const kind = inferActivityStopKind(a);
        if (kind === 'other') return;
        const explicitCount = getExplicitPassengerCount(a, kind);
        const delta = explicitCount ?? 1;
        runningPax += kind === 'pick' ? delta : -delta;
        runningPax = Math.max(0, runningPax);
        maxPax = Math.max(maxPax, runningPax);
      });

      const activeHours =
        shiftEnd > shiftStart
          ? (shiftEnd - shiftStart) / (1000 * 60 * 60)
          : 0;

      const validSorted = sorted.filter((a) => extractTimeMs(a) !== null);
      const nodes: ActivityNode[] = validSorted.flatMap((a, i) => {
        const timeMs = extractTimeMs(a)!;
        const isFirst = i === 0;
        const isLast = i === validSorted.length - 1;
        const kind = inferActivityStopKind(a);
        const boardingAlighting = inferActivityBoardingAndAlightingCounts(a);
        const hasBoarding = (boardingAlighting?.boarding ?? 0) > 0;
        const hasAlighting = (boardingAlighting?.alighting ?? 0) > 0;
        const timeLabel = formatTimeMs(timeMs);

        // Fan a stop visit into one node per passenger leg when the API returns
        // a single activity carrying multiple nested PICK/DROP trips (one per
        // passenger). Each emitted node holds a single-leg synthetic activity
        // so DriverActivityDetailPanel and the manifest export render per-rider
        // rows instead of collapsing N passengers into one event.
        const passengerLegs = extractPassengerLegsFromTrips(
          (a as Record<string, unknown>).trips
        );
        if (passengerLegs.length > 1) {
          return passengerLegs.map((leg) => {
            const legActivity = buildSyntheticActivityForLeg(a, leg);
            const legAt = ((leg as Record<string, unknown>).activity_type ?? '')
              .toString()
              .toUpperCase();
            const legType: ActivityNodeType = legAt === 'PICK' ? 'PICK' : 'DROP';
            return {
              type: legType,
              timeMs,
              label: `${legType} @ ${timeLabel}`,
              activity: legActivity,
            };
          });
        }

        // Mixed stops can legitimately board and alight in the same event.
        // Split them into two timeline nodes so pickup/dropoff counts stay accurate.
        if (hasBoarding && hasAlighting) {
          return [
            { type: 'PICK' as ActivityNodeType, timeMs, label: `PICK @ ${timeLabel}`, activity: a },
            { type: 'DROP' as ActivityNodeType, timeMs, label: `DROP @ ${timeLabel}`, activity: a },
          ];
        }

        let type: ActivityNodeType;
        if (kind === 'pick') type = 'PICK';
        else if (kind === 'drop') type = 'DROP';
        else if (isFirst) type = 'SHIFT_START';
        else if (isLast) type = 'SHIFT_END';
        else type = 'OTHER';
        const label =
          type === 'SHIFT_START'
            ? `Start @ ${timeLabel}`
            : type === 'SHIFT_END'
              ? `End @ ${timeLabel}`
              : type === 'OTHER'
                ? `Activity @ ${timeLabel}`
                : `${type} @ ${timeLabel}`;
        return [{ type, timeMs, label, activity: a }];
      });

      vehicles.push({
        vehicleId,
        displayName: vehicleId,
        activities: sorted,
        nodes,
        maxPax,
        totalActivities: nodes.filter((n) => n.type === 'PICK' || n.type === 'DROP').length,
        shiftStart,
        shiftEnd,
        activeHours,
        totalDistanceKm,
        emissionsKg: totalDistanceKm * co2RateKgPerKm,
        missingCoordinateSegments: missingVehicleCoordinateSegments,
      });
    });

    const totalDurationMs = simEndMs > simStartMs ? simEndMs - simStartMs : 0;

    // Sort by shift start and assign human-readable names. Prefer the label
    // resolved above (shift_name / vehicle_name / shift label registry) so
    // slot-named shifts like "DAY 1 COACH" surface in the results panel.
    // Falls back to "Vehicle N" by index when no label is available.
    const sortedVehicles = [...vehicles].sort((a, b) => a.shiftStart - b.shiftStart);
    sortedVehicles.forEach((v, i) => {
      const resolved = labelByVehicleKey.get(v.vehicleId);
      v.displayName = resolved && resolved.trim() !== '' ? resolved : `Vehicle ${i + 1}`;
    });

    return {
      vehicles: sortedVehicles,
      simStartMs,
      simEndMs,
      totalDurationMs,
      fleetTotalDistanceKm,
      fleetTotalEmissionsKg: fleetTotalDistanceKm * co2RateKgPerKm,
      co2RateKgPerKm,
      isCo2RateValid,
      co2RateError,
      missingCoordinateSegments,
      processingError: null,
    };
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'Unknown rendering error';
      return {
        vehicles: [],
        simStartMs: 0,
        simEndMs: 0,
        totalDurationMs: 0,
        fleetTotalDistanceKm: 0,
        fleetTotalEmissionsKg: 0,
        co2RateKgPerKm,
        isCo2RateValid,
        co2RateError,
        missingCoordinateSegments: 0,
        processingError: reason,
      };
    }
  }, [activities, options?.co2RateKgPerKm]);

  const hasLoggedInvalidRateRef = useRef(false);
  useEffect(() => {
    if (result.isCo2RateValid || hasLoggedInvalidRateRef.current) {
      return;
    }
    hasLoggedInvalidRateRef.current = true;
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.operations_co2_rate_invalid, {
      input_value: options?.co2RateKgPerKm == null ? 'empty' : String(options.co2RateKgPerKm),
      fallback_value: result.co2RateKgPerKm,
      timestamp: new Date().toISOString(),
    });
  }, [options?.co2RateKgPerKm, result.co2RateKgPerKm, result.isCo2RateValid]);

  const hasLoggedProcessingErrorRef = useRef(false);
  useEffect(() => {
    if (!result.processingError || hasLoggedProcessingErrorRef.current) {
      return;
    }
    hasLoggedProcessingErrorRef.current = true;
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.operations_view_render_fail, {
      feature_area: 'operations_view',
      error_category: 'render_failure',
      error_code: result.processingError.slice(0, 120),
      retryable: true,
      timestamp: new Date().toISOString(),
    });
  }, [result.processingError]);

  return result;
}
